import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import useReveal from '../../hooks/useReveal';

const MAP_EMBED_SRC =
  'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d56078.36876084634!2d77.36335300000002!3d28.542784!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce7274a6243cf%3A0x8b2b23439a9fc45e!2sStudioVS-2!5e0!3m2!1sen!2sin!4v1784043971303!5m2!1sen!2sin';

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_USER_TEMPLATE_ID =
  process.env.REACT_APP_EMAILJS_USER_TEMPLATE_ID || 'template_upyktfq';
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const contactDetails = [
  {
    label: 'Phone',
    value: '9211296162',
    href: 'tel:9211296162',
  },
  {
    label: 'Email',
    value: 'info@thestudiovs.com',
    href: 'mailto:info@thestudiovs.com',
  },
  {
    label: 'Studio',
    value: 'C-109, Sector 100, Noida',
    href: 'https://maps.google.com/?q=C-109,+Sector+100,+Noida',
  },
];

const INITIAL_FORM = {
  name: '',
  mail: '',
  phone: '',
  website: '',
  comment: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+]?[\d\s\-()]{7,15}$/;

const validateForm = (data) => {
  const errors = {};
  const name = data.name.trim();
  const mail = data.mail.trim();
  const phone = data.phone.trim();
  const comment = data.comment.trim();

  if (!name) errors.name = 'Please enter your name.';
  else if (name.length < 2) errors.name = 'Name must be at least 2 characters.';

  if (!mail) errors.mail = 'Please enter your email.';
  else if (!EMAIL_REGEX.test(mail)) errors.mail = 'Please enter a valid email address.';

  if (!phone) errors.phone = 'Please enter your phone number.';
  else if (!PHONE_REGEX.test(phone)) errors.phone = 'Please enter a valid phone number.';

  if (!comment) errors.comment = 'Please tell us about your project.';
  else if (comment.length < 10) errors.comment = 'Message must be at least 10 characters.';

  return errors;
};

const Contact = () => {
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: '', text: '' });
  const [isSending, setIsSending] = useState(false);

  useReveal(sectionRef, '.apple-contact-card, .apple-form, .apple-map', {
    y: 40,
    stagger: 0.1,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateForm(formData);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setStatus({
        type: 'error',
        text: 'Please fix the highlighted fields and try again.',
      });
      return;
    }

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setStatus({
        type: 'error',
        text: 'Email is not configured. Please add EmailJS keys to .env.local.',
      });
      return;
    }

    setIsSending(true);
    setStatus({ type: '', text: '' });

    try {
      const name = formData.name.trim();
      const email = formData.mail.trim();
      const phone = formData.phone.trim();
      const message = formData.comment.trim();
      const project = formData.website.trim() || 'Website inquiry';
      const now = new Date().toLocaleString('en-IN', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });

      await Promise.all([
        emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          { title: project, name, email, phone, project, message, time: now },
          EMAILJS_PUBLIC_KEY
        ),
        emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_USER_TEMPLATE_ID,
          { name, message, email },
          EMAILJS_PUBLIC_KEY
        ),
      ]);

      setStatus({
        type: 'success',
        text: 'Thank you for your message! We will get back to you soon.',
      });
      setFormData(INITIAL_FORM);
      setErrors({});
      setTimeout(() => setStatus({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatus({
        type: 'error',
        text: 'Something went wrong. Please try again or email us directly.',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="apple-section" id="contact" ref={sectionRef}>
      <div className="apple-wrap apple-center">
        <span className="apple-eyebrow">Let&apos;s build</span>
        <h2 className="apple-display">
          Get in <span className="apple-italic">touch</span>
        </h2>
        <p className="apple-lede">
          Share your site, brief, or idea — we respond with clarity on design,
          build, and next steps.
        </p>
      </div>

      <div className="apple-wrap">
        <div className="apple-contact-grid">
          {contactDetails.map((item) => (
            <a
              key={item.label}
              className="apple-contact-card"
              href={item.href}
              target={item.href.startsWith('http') ? '_blank' : undefined}
              rel={item.href.startsWith('http') ? 'noreferrer' : undefined}
            >
              <span className="label">{item.label}</span>
              <span className="value">{item.value}</span>
            </a>
          ))}
        </div>

        <div className="apple-contact-layout">
          <form className="apple-form" onSubmit={handleSubmit} noValidate>
            <h4>Send a message</h4>
            <p>Collaborations, consultations, and project discussions.</p>
            <div className="apple-form-grid">
              <div className="apple-field">
                <input
                  name="name"
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={isSending}
                  aria-invalid={Boolean(errors.name)}
                />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>
              <div className="apple-field">
                <input
                  name="mail"
                  type="email"
                  placeholder="Email address"
                  value={formData.mail}
                  onChange={handleInputChange}
                  disabled={isSending}
                  aria-invalid={Boolean(errors.mail)}
                />
                {errors.mail && <span className="field-error">{errors.mail}</span>}
              </div>
              <div className="apple-field">
                <input
                  name="phone"
                  type="tel"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={isSending}
                  aria-invalid={Boolean(errors.phone)}
                />
                {errors.phone && (
                  <span className="field-error">{errors.phone}</span>
                )}
              </div>
              <div className="apple-field">
                <input
                  name="website"
                  type="text"
                  placeholder="Project / company (optional)"
                  value={formData.website}
                  onChange={handleInputChange}
                  disabled={isSending}
                />
              </div>
              <div className="apple-field full">
                <textarea
                  name="comment"
                  placeholder="Tell us about your project..."
                  value={formData.comment}
                  onChange={handleInputChange}
                  disabled={isSending}
                  aria-invalid={Boolean(errors.comment)}
                />
                {errors.comment && (
                  <span className="field-error">{errors.comment}</span>
                )}
              </div>
            </div>
            <button type="submit" disabled={isSending}>
              {isSending ? 'Sending...' : 'Send message'}
            </button>
            {status.text && (
              <div
                className={
                  status.type === 'error'
                    ? 'contact-form-error'
                    : 'contact-form-success'
                }
              >
                {status.text}
              </div>
            )}
          </form>

          <div className="apple-map">
            <iframe
              title="TheStudioVS location"
              src={MAP_EMBED_SRC}
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
            <div className="apple-map-caption">
              <span>Visit the studio</span>
              <p>C-109, Sector 100, Noida</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
